import React, { useState } from 'react'
import { Copy, Download, Eye, EyeOff } from 'lucide-react'

interface JsonViewerProps {
  title: string
  data: any
  className?: string
}

export const JsonViewer: React.FC<JsonViewerProps> = ({ title, data, className }) => {
  const [collapsed, setCollapsed] = useState(false)

  const jsonString = JSON.stringify(data, null, 2)

  const handleCopy = () => {
    navigator.clipboard.writeText(jsonString)
  }

  const handleDownload = () => {
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${title.toLowerCase().replace(/\s+/g, '-')}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div className={`bg-white rounded shadow border ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          📄 {title}
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="p-1 hover:bg-gray-100 rounded"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <button
            onClick={handleCopy}
            className="p-1 hover:bg-gray-100 rounded"
            title="Copy JSON"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={handleDownload}
            className="p-1 hover:bg-gray-100 rounded"
            title="Download JSON"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {!collapsed && (
        <div className="p-3">
          <pre className="text-xs font-mono bg-gray-50 p-3 rounded border overflow-auto max-h-96 scrollbar-thin">
            {jsonString}
          </pre>
          
          {/* Summary */}
          <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
            <div className="grid grid-cols-2 gap-2">
              <div>Type: {typeof data}</div>
              <div>Size: {jsonString.length} chars</div>
              {Array.isArray(data) && <div>Items: {data.length}</div>}
              {typeof data === 'object' && data && !Array.isArray(data) && (
                <div>Keys: {Object.keys(data).length}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default JsonViewer
